import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import { CustomEditIcon } from '../../../../theme/icons/CustomEditIcon';
import FieldRow from '../components/FieldRow';
import TranslationRow from '../components/TranslationRow';
import { resolveValue } from '../utils/fieldValueUtils';
import type {
  PassContent,
  TemplateField,
  PassTemplateMeta,
} from '../../../../store/types/pass';
import type { FieldLocalisations } from '../../../../store/redux/slices/pass.slice';

interface FrontFieldsSectionProps {
  template?: PassTemplateMeta;
  passContent?: PassContent;
  isEdit?: boolean;
  fieldValues?: Record<string, string>;
  errors?: Record<string, string>;
  loyaltyBalance?: { enabled?: boolean; numberType?: string };
  hasLocalisation?: boolean;
  fieldsWithLocalisation?: Set<string>;
  templateLanguages?: string[];
  fieldLocalisations?: FieldLocalisations;
  expandedTranslations?: Record<string, boolean>;
  onFieldChange?: (fieldId: string, value: string) => void;
  onLocalisationChange?: (
    fieldId: string,
    locale: string,
    value: string
  ) => void;
  onAddTranslations?: (fieldId: string) => void;
  onRemoveTranslations?: (fieldId: string) => void;
  onFocus?: (fieldId: string, locale?: string | null) => void;
  focusedFieldId?: string | null;
  focusedLocale?: string | null;
}

const FrontFieldsSection = ({
  template,
  passContent,
  isEdit,
  fieldValues,
  errors = {},
  loyaltyBalance,
  hasLocalisation,
  fieldsWithLocalisation,
  templateLanguages = [],
  fieldLocalisations = {},
  expandedTranslations = {},
  onFieldChange,
  onLocalisationChange,
  onAddTranslations,
  onRemoveTranslations,
  onFocus,
  focusedFieldId,
  focusedLocale,
}: FrontFieldsSectionProps) => {
  if (!template) return null;

  const isStampCard = template.template_type === 'stampCard';

  const fields: TemplateField[] = [
    ...(!isStampCard ? [template.header.header_one] : []),
    template.header.header_two,
    template.header.header_three,
    template.primary.primary_one,
    template.primary.primary_two,
    template.secondary.sec_one,
    template.secondary.sec_two,
    template.secondary.sec_three,
    template.auxiliary.aux_one,
    template.auxiliary.aux_two,
    template.auxiliary.aux_three,
  ];

  const activeFields = fields.filter((f) => f.id);

  if (activeFields.length === 0) return null;

  return (
    <SectionWrapper
      title="Front fields"
      subtitle={
        <Text fontSize="15px" color="grey">
            {hasLocalisation
              ? 'These fields appear on the front of the pass. Add a translation for each language enabled on this template'
              : 'These fields appear on the front of the pass'}
        </Text>
      }
      openOnLoad
      id="front-fields-section"
    >
      <Box display="flex" flexDirection="column" gap={4}>
        {activeFields.map((field) => {
          const isLoyaltyField =
            loyaltyBalance?.enabled &&
            field.id === template?.header?.header_one?.id;
          const isDecimal = loyaltyBalance?.numberType === 'decimal';
          const isExpanded = expandedTranslations[field.id];
          const fieldTranslations = fieldLocalisations[field.id] || {};

          return (
            <Box key={field.id}>
              <FieldRow
                label={field.label || field.id}
                value={
                  fieldValues?.[field.id] ?? resolveValue(field, passContent)
                }
                error={errors[`fieldValues.${field.id}`]}
                placeholder={
                  isLoyaltyField ? (isDecimal ? '0.0' : '0') : undefined
                }
                isHighlighted={focusedFieldId === field.id && !focusedLocale}
                onFocus={() => onFocus?.(field.id, null)}
                inputMode={
                  isLoyaltyField
                    ? isDecimal
                      ? 'decimal'
                      : 'numeric'
                    : undefined
                }
                onChange={(value) => {
                  if (isLoyaltyField) {
                    const pattern = isDecimal ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
                    if (value !== '' && !pattern.test(value)) return;
                    if (isDecimal && value.startsWith('.')) {
                      onFieldChange?.(field.id, `0${value}`);
                      return;
                    }
                    if (isDecimal && value.startsWith('-.')) {
                      onFieldChange?.(field.id, `-0${value.slice(1)}`);
                      return;
                    }
                  }
                  onFieldChange?.(field.id, value);
                }}
              />

              {hasLocalisation && templateLanguages.length > 0 && fieldsWithLocalisation?.has(field.id) && (
                <>
                  {isExpanded ? (
                    <Box display="flex" flexDirection="column" gap={2} mt={2}>
                      {templateLanguages.map((locale) => {
                        const hasAnyTranslation = templateLanguages.some((l) => l !== locale && fieldTranslations[l]?.trim());
                        return (
                        <TranslationRow
                          key={locale}
                          locale={locale}
                          value={fieldTranslations[locale] || ''}
                          required={hasAnyTranslation && !fieldTranslations[locale]?.trim()}
                          error={
                            errors[`localisation.${field.id}.${locale}`]
                              ? 'Translation is required'
                              : undefined
                          }
                          isHighlighted={focusedFieldId === field.id && focusedLocale === locale}
                          onFocus={() => onFocus?.(field.id, locale)}
                          onChange={(value) =>
                            onLocalisationChange?.(field.id, locale, value)
                          }
                        />
                      );
                      })}
                      <Text
                        fontSize="12px"
                        color="#FF4935"
                        textAlign="right"
                        cursor="pointer"
                        mt={3}
                        onClick={() => onRemoveTranslations?.(field.id)}
                        _hover={{ opacity: 0.8 }}
                      >
                        Remove translations
                      </Text>
                    </Box>
                  ) : (
                    <Flex
                      align="center"
                      gap={1.5}
                      fontSize="15px"
                      color="inkBlue"
                      cursor="pointer"
                      mt={3}
                      pl={2}
                      onClick={() => onAddTranslations?.(field.id)}
                      _hover={{ opacity: 0.7 }}
                    >
                      {isEdit ? (
                        <>
                          <CustomEditIcon boxSize="14px" />
                          Edit translation
                        </>
                      ) : (
                        '+ Add translation'
                      )}
                    </Flex>
                  )}
                </>
              )}

              {hasLocalisation && fieldsWithLocalisation?.has(field.id) && isExpanded && <Divider mt={4} />}
              {hasLocalisation && fieldsWithLocalisation?.has(field.id) && !isExpanded && <Box mb={3} />}
            </Box>
          );
        })}
      </Box>
    </SectionWrapper>
  );
};

export default FrontFieldsSection;
